import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { Row, Col, Input, Popover, Checkbox, Popconfirm, Select } from 'antd'
import { SaveFilled, CloseOutlined, CheckOutlined, DeleteFilled } from '@ant-design/icons'
import { useOutletContext, useParams, useSearchParams } from 'react-router-dom'
import DetailSubRuleTable from '../../../Components/Omnichannel/DetailSubRuleTable'
import MenuIcon from '../../../Components/Svg/MenuIcon'
import ClearFilters from '../../../Components/Table/TableComponents/ClearFilters'
import { TranslationKeys } from '../../../Constants/TranslationKeys'
import {
  getHtmlParentElement,
  handleModalTriggerEvent,
  renderTranslation,
  stopPropagation,
  sendMessage,
} from '../../../Helpers/GenericHelpers'
import { setCapitalLetter } from '../../../Helpers/StringHelpers'
import {
  DateFormat,
  FilterLangClass,
  PredefinedFilters,
  TimeFormat,
  maxRuleLength,
  subruleDropdownAll,
  subruleDropdownModified,
  visibilityParam,
} from '../../../Constants/Configuration'
import { getActiveFilterValue } from '../../../Helpers/FilterHelper'
import {
  saveFilterColumns,
  saveSubruleFilters,
  setFilterDiscountVisibilityDropdownValue,
  setSeasonFilter,
  setSubRuleId,
} from '../../../Slices/Omnichannel/SubRuleSlice'
import {
  deleteRule,
  populateSubRuleTableWithArticles,
  saveSeasonSubrule,
} from '../../../Services/Omnichannel/SeasonRuleService'
import {
  isRuleEditToDisable,
  isRuleNameMaxLengthValid,
  isRuleNameMinLengthValid,
  isRuleNameUnique,
  retrieveSubrule,
  retrieveSeasonRuleSubrulesList,
  retrieveRuleFromSubrule,
  isRuleLockedByOtherUsers,
  isRuleLockedByCurrentUser,
} from '../../../Helpers/DiscountHelper'
import ConfirmSeasonRuleChangeModal from '../../../Components/Omnichannel/ConfirmSeasonRuleChangeModal'
import { toggleSeasonConfirmChangeModal } from '../../../Slices/Omnichannel/SeasonRuleSlice'
import { useAppDispatch, useAppSelector } from '../../../hooks'
import { useNavigateWrapper } from '../../../Helpers/NavigateHelper'
import { retrieveColumnKey } from '../../../Helpers/TableHelper'
import { TypeOfLock, acquireLock } from 'Services/Omnichannel/LockService'
import TagContainer from 'Components/Omnichannel/TagContainer'
import { customEqual as lockEquality } from 'Helpers/LockHelper'
import ExcelButton from 'Components/Generic/MenuExcel'
import dayjs from 'dayjs'
import { TypeOfChannel } from 'Slices/Omnichannel/DiscountSlice'
import { BaseStorePricelist } from 'Constants/Configuration'
import { getPricelistName } from 'Helpers/PricesHelper'

function SubRuleDetail(props) {
  const dispatch = useAppDispatch()
  const navigationWrapper = useNavigateWrapper()
  const language = useAppSelector(state => state.present.language.value)
  const filterColumns = useAppSelector(state => state.present.subRule.filterColumns)
  const subRuleId = useAppSelector(state => state.present.subRule.subRuleId)
  const seasonRuleList = useAppSelector(state => state.present.seasonRule.seasonRuleList)
  const listingsDiscountToSave = useAppSelector(state => state.present.subRule.listingsDiscountToSave)
  const filterDiscountVisibilityDropdownValue = useAppSelector(
    state => state.present.subRule.filterDiscountVisibilityDropdownValue,
  )
  const user = useAppSelector(state => state.present.configuration.userValue)
  const locks = useAppSelector(state => state.present.locks.locks, lockEquality)
  const { id } = useParams()
  const [subRuleName, setSubRuleName] = useState('')
  const [popoverMenuVisible, setPopoverMenuVisible] = useState(false)
  const [selectedFilters, setSelectedFilters] = useState(JSON.parse(JSON.stringify(filterColumns)))
  const [searchParams, setSearchParams] = useSearchParams()
  const [updatedFilterColumns, setUpdatedFilterColumns] = useState(JSON.parse(JSON.stringify(filterColumns)))
  const [paginationOff, setPaginationOff] = useState(false)
  const typeOfChannel: TypeOfChannel = useAppSelector(state => state.present.discount.typeOfChannel)

  const [currentSubRule, ruleSubrules] = useMemo(() => {
    const subruleMatch = retrieveSubrule(subRuleId, seasonRuleList) as any
    const subrulesMatch = retrieveSeasonRuleSubrulesList(subRuleId, seasonRuleList)

    if (subruleMatch) {
      setSubRuleName(subruleMatch?.name === BaseStorePricelist ? getPricelistName(subruleMatch?.name, language) : subruleMatch?.name)
    }
    return [subruleMatch, subrulesMatch]
  }, [seasonRuleList, subRuleId, language])

  const isCarryOver = currentSubRule?.season?.carryOver
  const bodyrefContext = useOutletContext() as React.MutableRefObject<undefined>

  useEffect(() => {
    if (id) {
      const subruleId = parseInt(id)
      dispatch(setSubRuleId(subruleId))
    } else {
      dispatch(setSubRuleId(undefined))
    }
  }, [dispatch, id])

  useEffect(() => {
    if (Object.keys(currentSubRule ?? {}).length > 0) {
      dispatch(setSeasonFilter(currentSubRule?.season?.name))

      if (searchParams) {
        searchParams.forEach((value, key) => {
          if (key !== visibilityParam) {
            dispatch(saveSubruleFilters({ key: retrieveColumnKey(key), value: value }))
          }
        })
      }

      const visibility = searchParams.get(visibilityParam)

      dispatch(
        setFilterDiscountVisibilityDropdownValue(
          currentSubRule?.virtualId || visibility ? subruleDropdownAll : subruleDropdownModified,
        ),
      )

      dispatch(populateSubRuleTableWithArticles(false))
    }
  }, [currentSubRule, dispatch, searchParams])

  const onSubRuleNameChange = useCallback(e => {
    const newName = e.target.value
    if (isRuleLockedByCurrentUser(locks, currentSubRule?.id, TypeOfLock.Omnichannel, user)) {
      setSubRuleName(newName)
    } else {
      dispatch(acquireLock(false)).then(x => {
        if (x) {
          setSubRuleName(newName)
        }
      })
    }
  }, [])

  useEffect(() => {
    const seasonIndex = updatedFilterColumns.predefinedFilters.findIndex(x => x.value === 'season')
    updatedFilterColumns.predefinedFilters[seasonIndex].visible = false
    if (isCarryOver) {
      updatedFilterColumns.predefinedFilters[seasonIndex].visible = true
    }
    setSelectedFilters(updatedFilterColumns)
  }, [isCarryOver, updatedFilterColumns])

  const isSubruleVirtual = currentSubRule && currentSubRule.virtualId

  const editDisabled = useMemo(() => {
    const rule = retrieveRuleFromSubrule(currentSubRule?.virtualId ?? currentSubRule?.id, seasonRuleList) as any
    const isRuleActive = rule?.isActive
    const islocked = isRuleLockedByOtherUsers(
      locks,
      currentSubRule?.virtualId ?? currentSubRule?.id,
      TypeOfLock.Omnichannel,
      user,
    )
    return islocked || isRuleEditToDisable(rule, isRuleActive)
  }, [currentSubRule?.id, currentSubRule?.virtualId, locks, seasonRuleList, user])

  const isRuleNameLengthValid = isRuleNameMaxLengthValid(subRuleName) && isRuleNameMinLengthValid(subRuleName)
  const isRuleUnique = isRuleNameUnique(
    ruleSubrules,
    subRuleName,
    !isSubruleVirtual ? currentSubRule?.id : currentSubRule.virtualId,
    editDisabled,
  )
  const isRuleNameUniqueAndValid = !!(isRuleUnique && isRuleNameLengthValid)
  const isRuleToBeSaved = isSubruleVirtual
    ? isRuleNameUniqueAndValid && (listingsDiscountToSave.length > 0 || subRuleName !== (currentSubRule?.name === BaseStorePricelist ? getPricelistName(currentSubRule?.name, language) : currentSubRule?.name))
    : !editDisabled &&
      (((subRuleName !== currentSubRule?.name && subRuleName !== BaseStorePricelist) && isRuleNameUniqueAndValid) || listingsDiscountToSave.length > 0)

  const visiblePredefinedFilters = useMemo(() => {
    return selectedFilters.predefinedFilters.filter(filter => filter.visible)
  }, [selectedFilters.predefinedFilters])

  const onChangeFilterCheckbox = useCallback(
    (checkedValues, filterGroup) => {
      const currentFilters = JSON.parse(JSON.stringify(updatedFilterColumns))
      for (const group in currentFilters) {
        if (group === filterGroup) {
          const filteredGroup = currentFilters[group]
          for (const filter in filteredGroup) {
            if (checkedValues.indexOf(filteredGroup[filter].value) !== -1) {
              filteredGroup[filter].active = true
            } else {
              filteredGroup[filter].active = false
            }
          }
        }
      }

      setSelectedFilters(currentFilters)
    },
    [filterColumns],
  )

  const saveFilter = useCallback(
    e => {
      stopPropagation(e)
      setPopoverMenuVisible(false)
      dispatch(saveFilterColumns(JSON.parse(JSON.stringify(selectedFilters))))

      dispatch(populateSubRuleTableWithArticles(false))
    },
    [dispatch, selectedFilters],
  )

  const closeFilter = useCallback(
    e => {
      stopPropagation(e)
      setSelectedFilters(JSON.parse(JSON.stringify(selectedFilters)))
      setPopoverMenuVisible(false)
    },
    [selectedFilters],
  )

  const subRuleCheckboxTitle = useMemo(
    () => (
      <Row onClick={stopPropagation}>
        <Col span={24}>
          <div>
            {renderTranslation(language, TranslationKeys.quantitiesCheckboxTitle)}
            <span className='float-right'>
              <CheckOutlined className='quantities-check-icon general-check-icon' onClick={saveFilter} />
              <CloseOutlined className='quantities-close-icon general-clear-icon' onClick={closeFilter} />
            </span>
          </div>
        </Col>
      </Row>
    ),
    [language, saveFilter, closeFilter],
  )

  const subRuleCheckboxContent = useMemo(() => {
    return (
      <Checkbox.Group
        className='subrule-checkbox-container'
        onChange={checkedValues => {
          onChangeFilterCheckbox(checkedValues, PredefinedFilters)
        }}
        value={getActiveFilterValue(selectedFilters.predefinedFilters)}
      >
        <Row onClick={stopPropagation}>
          {visiblePredefinedFilters.map(({ value, active, visible }) => {
            return (
              <Col key={value} span={24}>
                {
                  <Checkbox className={'subrule-checkbox general-checkbox'} value={value}>
                    {renderTranslation(language, TranslationKeys[FilterLangClass + setCapitalLetter(value)])}
                  </Checkbox>
                }
              </Col>
            )
          })}
        </Row>
      </Checkbox.Group>
    )
  }, [language, onChangeFilterCheckbox, selectedFilters, visiblePredefinedFilters])

  const saveSubRule = useCallback(() => {
    if (editDisabled || !isRuleUnique || !isRuleToBeSaved) {
      return
    }
    const subRuleToSave = { ...currentSubRule }
    subRuleToSave.name = subRuleName

    searchParams.delete(visibilityParam)
    dispatch(saveSeasonSubrule(subRuleToSave, navigationWrapper, searchParams)).then(() => {
      if (typeOfChannel === TypeOfChannel.Store && listingsDiscountToSave?.length > 0) {
        sendMessage({ type: 'Omnichannel', ruleId: subRuleToSave.id })
      }
    })
  }, [
    editDisabled,
    isRuleUnique,
    isRuleToBeSaved,
    currentSubRule,
    subRuleName,
    searchParams,
    dispatch,
    navigationWrapper,
    typeOfChannel,
    listingsDiscountToSave,
  ])

  const deleteSubRule = useCallback(
    e => {
      stopPropagation(e)
      if (!editDisabled) dispatch(deleteRule(currentSubRule, true, navigationWrapper))
    },
    [editDisabled, dispatch, currentSubRule, navigationWrapper],
  )

  const filterTableByModifiedValues = useCallback(
    value => {
      dispatch(setFilterDiscountVisibilityDropdownValue(value))
      dispatch(populateSubRuleTableWithArticles(false))
    },
    [dispatch],
  )

  const modalTriggerClickHandler = useCallback(
    event => {
      const showModalCallback = () => {
        event.preventDefault()
        event.stopPropagation()
        dispatch(toggleSeasonConfirmChangeModal(true))
      }

      const noChangesCallback = () => {
        if (!isRuleToBeSaved && isSubruleVirtual) {
          dispatch(deleteRule(currentSubRule, true, null))
        }
      }

      const extraRef = document.querySelector('.collapse-container')

      handleModalTriggerEvent(
        event,
        bodyrefContext?.current,
        isRuleToBeSaved,
        showModalCallback,
        noChangesCallback,
        extraRef,
      )
    },
    [bodyrefContext, isRuleToBeSaved, dispatch, isSubruleVirtual, currentSubRule],
  )

  useEffect(() => {
    document.addEventListener('click', modalTriggerClickHandler, true)
    return () => {
      document.removeEventListener('click', modalTriggerClickHandler, true)
    }
  }, [modalTriggerClickHandler])

  const isRuleLocked = useMemo(
    () => isRuleLockedByOtherUsers(locks, currentSubRule?.id, TypeOfLock.Omnichannel, user),
    [currentSubRule?.id, locks, user],
  )

  return currentSubRule ? (
    <div>
      <ConfirmSeasonRuleChangeModal
        onChangeConfirm={saveSubRule}
        bodyref={bodyrefContext}
        onChangeUndo={() => setSubRuleName(currentSubRule.name)}
      />
      <div className='title-box'>
        <div className='ant-row ant-row-baseline'>
          <div className='ant-col'>{renderTranslation(language, TranslationKeys.subRuleBoxTitle)}</div>
          {!editDisabled ? (
            <SaveFilled
              className={'generic-icon ml-0 save-icon channel-save-icon' + (isRuleToBeSaved ? ' active' : '')}
              onClick={saveSubRule}
            />
          ) : null}
        </div>
        {isRuleLocked ? (
          <div className='rule-box-editing'>{renderTranslation(language, TranslationKeys.ruleBoxEditing)}</div>
        ) : null}
        <Popover
          content={renderTranslation(language, TranslationKeys.subRuleBoxPopoverSubRuleDeleteRule)}
          trigger='hover'
          placement='left'
        >
          <Popconfirm
            key={'popconfirm-delete-active-rule'}
            title={renderTranslation(language, TranslationKeys.subRuleBoxPopoverSubRuleDeleteRuleTitle)}
            onConfirm={deleteSubRule}
            onCancel={stopPropagation}
            okText={renderTranslation(language, TranslationKeys.configurationDropdownPopYes)}
            cancelText={renderTranslation(language, TranslationKeys.configurationDropdownPopNo)}
            disabled={isSubruleVirtual ? false : editDisabled}
            placement='bottomRight'
            getPopupContainer={getHtmlParentElement}
          >
            {!editDisabled ? (
              <DeleteFilled className={'generic-icon icon-rule-box '} onClick={stopPropagation} />
            ) : null}
          </Popconfirm>
        </Popover>
      </div>
      <Row>
        <Col span={8} className='sub-rule-form'>
          <div className='upper-label'>{renderTranslation(language, TranslationKeys.subRuleBoxSubRuleName)}</div>
          <Input
            placeholder={renderTranslation(language, TranslationKeys.subRuleBoxSubRuleNamePlaceholder)}
            maxLength={maxRuleLength}
            value={subRuleName}
            onChange={onSubRuleNameChange}
            status={!editDisabled && !isRuleNameUniqueAndValid ? 'error' : undefined}
            allowClear={{ clearIcon: <CloseOutlined className='general-icon input-clear-icon' /> }}
            disabled={editDisabled}
          />
          {
            <div className='lower-label input-warning'>
              {!editDisabled && !isRuleNameLengthValid
                ? renderTranslation(language, TranslationKeys.warningSeasonTextCharLength)
                : !editDisabled && !isRuleUnique
                ? renderTranslation(language, TranslationKeys.warningSeasonNameAlreadyTaken)
                : null}
            </div>
          }
        </Col>
      </Row>
      <Row>
        <Col span={14} className='sub-rule-filter-box'>
          <Popover
            content={renderTranslation(language, TranslationKeys.filterSettingsTips)}
            trigger='hover'
            placement={'right'}
          >
            <Popover
              id='subRuleFilterMenu'
              title={subRuleCheckboxTitle}
              content={subRuleCheckboxContent}
              placement='bottomLeft'
              autoAdjustOverflow={true}
              trigger='click'
              open={popoverMenuVisible}
              onOpenChange={setPopoverMenuVisible}
              getPopupContainer={getHtmlParentElement}
            >
              <MenuIcon onClick={stopPropagation} className={'generic-icon menu-icon'} />
            </Popover>
          </Popover>
          <ClearFilters />
          <TagContainer />
        </Col>
        <Col span={10} className='sub-rule-filter-box' style={{ textAlign: 'right' }}>
          <Select
            value={filterDiscountVisibilityDropdownValue}
            onChange={filterTableByModifiedValues}
            className='section-dropdown'
            options={[
              { label: renderTranslation(language, TranslationKeys.subRuleViewAll), value: subruleDropdownAll },
              {
                label: renderTranslation(language, TranslationKeys.subRuleViewOnlyModified),
                value: subruleDropdownModified,
              },
            ]}
            getPopupContainer={getHtmlParentElement}
          ></Select>{' '}
          {!isSubruleVirtual ? (
            <ExcelButton
              filename='Omnichannel-subrule-'
              RowHead={{
                RuleName: currentSubRule?.name,
                Season: currentSubRule?.season?.name,
                DateStart: dayjs(currentSubRule?.startDateTime).format(`${DateFormat} ${TimeFormat}`),
                DateEnd: dayjs(currentSubRule?.endDate).format(`${DateFormat} ${TimeFormat}`),
                DecimalRound: currentSubRule?.ruleDecimalPrice,
                RuleType: currentSubRule?.ruleType,
              }}
              RuleId={subRuleId}
              subrule={true}
              editDisabled={editDisabled}
              typeOfChannel={typeOfChannel}
              onTriggerClick={() => setPaginationOff(true)}
            />
          ) : null}
        </Col>
      </Row>
      <Row>
        <Col span={24} className='sub-rule-table-box'>
          <DetailSubRuleTable editDisabled={editDisabled} isCarryOver={isCarryOver} paginationOff={paginationOff} />
        </Col>
      </Row>
    </div>
  ) : null
}

export default SubRuleDetail
